import {useQuery} from 'react-query'
import {fetchStudentDailyTrip} from '../liveTrip/api'
import {RunnerBall} from '../../../components/FormLoader'
import {Error500} from '../../modules/errors/components/Error500'
import {DailyTripAPIData, StudentDailyTripAPIData} from '../../ts/trip'
import {compareAsc, parse} from 'date-fns'
import mapboxgl, {Map} from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import {useEffect, useRef} from 'react'

interface TripRouteProps {
  tripData: DailyTripAPIData
}

function TripRoute({tripData}: TripRouteProps) {
  const mapRef = useRef<Map | null>(null)
  const mapContainerRef = useRef<HTMLDivElement | null>(null)

  const {
    data: response,
    isLoading,
    isError,
  } = useQuery(
    ['student_daily_trip', tripData.daily_trip_id],
    () => fetchStudentDailyTrip(tripData.daily_trip_id),
    {
      refetchOnWindowFocus: false,
    }
  )

  /**
   * Initializes the Mapbox map.
   */
  const initializeMap = () => {
    if (!process.env.REACT_APP_MAPBOX_TOKEN) {
      console.error('Missing Mapbox Token')
      return null
    }
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN

    if (!tripData.start_location_longitude || !tripData.start_location_latitude) {
      console.error('Missing trip start location coordinates')
      return null
    }

    const map = new mapboxgl.Map({
      container: mapContainerRef.current as HTMLDivElement,
      center: [tripData.start_location_longitude, tripData.start_location_latitude],
      // center: [76.2999, 9.9816], // Marine Drive, Ernakulam
      zoom: 12.5,
      maxZoom: 19,
      pitch: 60,
      attributionControl: false,
    })

    return map
  }

  /**
   * Draws the route on the map.
   */
  const drawRoute = (map: Map, coordinates: number[][]) => {
    if (!map || !coordinates.length) return

    // Add the route as a polyline
    map.on('load', () => {
      map.addSource('route', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates,
          },
        },
      })

      map.addLayer({
        id: 'route',
        type: 'line',
        source: 'route',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#3b82f6',
          'line-width': 4,
        },
      })
      map.loadImage('/media/icons/map-direction-blue-icon.png', (error, image) => {
        if (error || !image) {
          console.error('Error loading arrow image:', error)
          return
        }

        // Add image to the map
        if (!map.hasImage('arrow')) {
          map.addImage('arrow', image)
        }

        // Add directional arrows along the route
        map.addLayer({
          id: 'route-arrows',
          type: 'symbol',
          source: 'route',
          layout: {
            'symbol-placement': 'line',
            'symbol-spacing': 300, // Adjust spacing between arrows
            'icon-image': 'arrow',
            'icon-size': 0.3,
          },
        })
      })
    })
  }

  /**
   * Adds markers for start, stop, and end locations.
   */
  const addMarkers = (map: Map, coordinates: [number, number][]) => {
    if (!map || !coordinates.length) return

    // const startElem = document.createElement('i')

    // startElem.classList.add(
    //   'bi',
    //   'bi-record-circle',
    //   'fs-1',
    //   'text-info'
    //   // 'bg-white',
    //   // 'p-2',
    //   // 'rounded'
    // )

    // Add start marker
    new mapboxgl.Marker({color: '#3ad080'}).setLngLat(coordinates[0]).addTo(map)

    // Add stop markers
    coordinates.slice(1, -1).forEach((coord) => {
      new mapboxgl.Marker({color: '#f5ce2b', scale: 0.8}).setLngLat(coord).addTo(map)
    })

    // Add end marker
    new mapboxgl.Marker({color: '#ff6060'})
      .setLngLat(coordinates[coordinates.length - 1])
      .addTo(map)
  }

  useEffect(() => {
    if (mapContainerRef.current && !isLoading && response) {
      const mapInstance = initializeMap()
      if (mapInstance) {
        mapRef.current = mapInstance

        const presentStudents = response?.data
          ?.filter((studentTrip) => Number(studentTrip.is_present) === 1)
          .sort((a, b) => {
            if (!a.checkin_time || !b.checkin_time) return 0
            return compareAsc(
              parse(a.checkin_time, 'HH:mm:ss', new Date()),
              parse(b.checkin_time, 'HH:mm:ss', new Date())
            )
          })

        // dummy data for testing
        // const coordinates: [number, number][] = [
        //   [76.2999, 9.9816], // Marine Drive, Ernakulam
        //   [76.2865, 9.971], // M.G Road
        //   [76.2764, 9.9623], // Kadavanthra
        //   [76.2705, 9.9526], // Vyttila
        //   [76.2657, 9.9412], // Palarivattom
        //   [76.256, 9.9314], // Edappally
        //   [76.2478, 9.9172], // Kalamassery
        //   [76.2341, 9.8935], // Angamaly
        //   [76.2417, 9.9034], // Aluva
        //   [76.2999, 9.9816], // Marine Drive, Ernakulam
        // ]

        // Extract coordinates for the route
        const coordinates: [number, number][] = [
          [Number(tripData.start_location_longitude), Number(tripData.start_location_latitude)], // Start
          // Stops
          ...(presentStudents
            ?.map((student: StudentDailyTripAPIData) => {
              const longitude = student.checkin_location_longitude
              const latitude = student.checkin_location_latitude
              return [longitude, latitude]
            })
            .filter(Boolean) as [number, number][]),
          [Number(tripData.end_location_longitude), Number(tripData.end_location_latitude)], // End
        ]

        // Draw the route and add markers

        drawRoute(mapInstance, coordinates)
        addMarkers(mapInstance, coordinates)
      }
    }

    return () => {
      mapRef.current?.remove()
    }
  }, [mapContainerRef.current, isLoading, response])

  if (isLoading) {
    return <RunnerBall />
  }
  if (isError) {
    return (
      <div className="text-center">
        <Error500 />
      </div>
    )
  }

  return <div id="map-container" ref={mapContainerRef} style={{height: '75vh'}} />
}

export default TripRoute
